import { map, Observable, BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';

export interface INotificationResponse {
  message: string;
  payload: INotificationPayload;
}

export interface INotificationPayload {
  content: INotificationContent[];
  page: INotificationPage;
}

export interface INotificationContent {
  id: string;
  content: string;
  createdAt: string;
  senderName: string;
  senderId: string;
  readAt: string | null;
  caseId: string;
  read: boolean;
  complaintapplicationNumber: string;
}

export interface INotificationPage {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private notificationsSubject = new BehaviorSubject<INotificationContent[]>(
    []
  );
  private unreadCountSubject = new BehaviorSubject<number>(0);

  constructor(private api: ApiService) {}

  getNotifications(): Observable<INotificationContent[]> {
    return this.api
      .GET<INotificationResponse>('notifications')
      .pipe(
        map((response) => {
          const notifications = response.payload.content;
          this.notificationsSubject.next(notifications);
          this.updateUnreadCount(notifications);
          return notifications;
        })
      );
  }

  getNotificationsObservable(): Observable<INotificationContent[]> {
    return this.notificationsSubject.asObservable();
  }

  markAsRead(notificationId: string): Observable<INotificationResponse> {
    const body = {};

    return this.api
      .PATCH<INotificationResponse>(
        `notifications/${notificationId}/read`,
        body
      )
      .pipe(
        map((response) => {
          // Update the local notifications array immediately
          const currentNotifications = this.notificationsSubject.value;
          const updatedNotifications: INotificationContent[] =
            currentNotifications.map((notification) =>
              notification.id === notificationId
                ? {
                    ...notification,
                    read: true,
                    readAt: new Date().toISOString(),
                  }
                : notification
            );
          this.notificationsSubject.next(updatedNotifications);
          this.updateUnreadCount(updatedNotifications);
          return response;
        })
      );
  }

  getUnreadCount(): Observable<number> {
    return this.unreadCountSubject.asObservable();
  }

  private updateUnreadCount(notifications: INotificationContent[]): void {
    const unreadCount = notifications.filter(
      (notification) => !notification.read
    ).length;
    this.unreadCountSubject.next(unreadCount);
  }

  // Method to refresh notifications from server
  refreshNotifications(): void {
    this.getNotifications().subscribe();
  }
}
